import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';

import { Card, CardContent, CardActions, Button, Divider, Tooltip, IconButton, lighten, useTheme } from '@mui/material';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import { theme } from '../../../utils/theme';

// mui icons
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import UnarchiveRoundedIcon from '@mui/icons-material/UnarchiveRounded';
import StopCircleRoundedIcon from '@mui/icons-material/StopCircleRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import PunchClockRoundedIcon from '@mui/icons-material/PunchClockRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import MapsUgcRoundedIcon from '@mui/icons-material/MapsUgcRounded';
import ManageSearchRoundedIcon from '@mui/icons-material/ManageSearchRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';

// stores
import useMessageDialog from '../../../store/messages';
import useSnackbarStore from '../../../store/snackbar';

import { BasicFlex, InputLabel, Notes } from '../../../utils/Common';

import { formatDurationFromSeconds, formatGenericDate } from '../../../utils/util';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { updateStage } from '../apiService';

// timeline
import TimelineWithComments from '../../../utils/utilities/Timeline/TimelineWithComments';

import Dialog from '../../../utils/utilities/Dialog';

import { sendMessage } from '../../Messages/apiService';

/* -------------------------------------------------------------------------- */
/*  Status config — single source of truth                                    */
/* -------------------------------------------------------------------------- */

const STATUS_CONFIG = {
  scheduled:   { label: "Scheduled",  dotColor: "#888780", textColor: "#5F5E5A", barColor: "#888780" },
  accepted:    { label: "Pending",    dotColor: "#BA7517", textColor: "#854F0B", barColor: "#BA7517" },
  in_progress: { label: "Processing", dotColor: "#378ADD", textColor: "#185FA5", barColor: "#378ADD" },
  paused:      { label: "Paused",     dotColor: "#EF9F27", textColor: "#854F0B", barColor: "#EF9F27" },
  completed:   { label: "Completed",  dotColor: "#1D9E75", textColor: "#0F6E56", barColor: "#1D9E75" },
};

/* -------------------------------------------------------------------------- */
/*  Styled components                                                         */
/* -------------------------------------------------------------------------- */

const TicketCard = styled(Card)`
  width: 100%;
  border: 0.5px solid ${props => props.theme.palette.divider};
  border-radius: 12px;
  overflow: hidden;
  background-color: ${props => props.$expanded ? lighten(props.theme.colors.primaryFaint, 0.8) : "#fff"};
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }
`;

const Header = styled.div`
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 0.5px solid ${props => props.theme.palette.divider};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1;
`;

const TicketNum = styled.span`
  font-size: 1.2rem;
  font-weight: 500;
  color: ${props => props.theme.colors.mildText};
  flex-shrink: 0;
`;

/* Wig code — promoted: larger, monospace, with tag icon */
const WigCode = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 4px;
  background-color: ${props => props.theme.colors.lightBg || "#F1EFE8"};
  border-radius: 6px;
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 1.5rem;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: ${props => props.theme.colors.text || "#2C2C2A"};
  min-width: 0;

  svg {
    font-size: 1.5rem;
    color: ${props => props.theme.colors.mildText};
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const StatusPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  border-radius: 999px;
  background-color: ${props => `${props.$dotColor}14`};
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: ${props => props.$textColor};
  text-transform: uppercase;
  white-space: nowrap;
`;

const StatusDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  flex-shrink: 0;
`;

const Body = styled(CardContent)`
  padding: 14px !important;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ClientRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.palette.info.light};
  color: ${props => props.theme.palette.info.dark};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 1.3rem;
  flex-shrink: 0;
`;

const ClientInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const ClientName = styled.div`
  font-size: 1.6rem;
  font-weight: 500;
  letter-spacing: 0.2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ClientMeta = styled.div`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.mildText};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Details panel with service / appointment / rep */
const DetailsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 12px;
  background-color: ${props => props.theme.colors.lightBg || "#F8F7F2"};
  border-radius: 8px;
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 1.25rem;

  & + & {
    padding-top: 8px;
    border-top: 0.5px solid ${props => props.theme.palette.divider};
  }
`;

const DetailLabel = styled.span`
  color: ${props => props.theme.colors.mildText};
  flex-shrink: 0;
`;

const DetailValue = styled.span`
  font-weight: 500;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

/* Progress — promoted: larger track, count above-right, label */
const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const ProgressLabel = styled.span`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.mildText};
  font-weight: 500;
`;

const ProgressCount = styled.span`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-variant-numeric: tabular-nums;
`;

const PBTrack = styled.div`
  height: 8px;
  border-radius: 999px;
  background-color: ${props => props.theme.colors.darkBg};
  overflow: hidden;
`;

const PBFill = styled.div`
  height: 100%;
  background-color: ${props => props.$color || "#1D9E75"};
  width: ${props => props.$width || "0%"};
  border-radius: 999px;
  transition: width 0.3s ease;
`;

const TimerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.mildText};

  svg {
    font-size: 1.4rem;
  }
`;

/* Footer */
const Footer = styled(CardActions)`
  padding: 8px 10px !important;
  border-top: 0.5px solid ${props => props.theme.palette.divider};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const QuickActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const PrimaryActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-grow: 1;
  justify-content: flex-end;
`;

const TicketTimeline = styled.div`
  margin-top: 8px;
`;

const AlertText = styled.p`
  font-size: 1.5rem;
  padding: 0 1.6rem;
  line-height: 1.6;
  color: ${props => props.theme.colors.mildText};
`;

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const calculateTotal = (status, activeStartTime, initialSeconds) => {
  if (status === 'in_progress' && activeStartTime) {
    const startTime = new Date(activeStartTime).getTime();
    const now = new Date().getTime();
    const diffInSeconds = Math.floor((now - startTime) / 1000);
    return initialSeconds + diffInSeconds;
  }
  return initialSeconds;
};

const getFirstName = (name) => name?.split(' ')[0] || '';

const getInitials = (name) => {
  if (!name) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

const KanbanTicket = ({
  ticket = {},
  receive = false,
  onClick = () => {},
  expanded = false,
  setTicket,
  setOpen,
  vendor,
}) => {
  const [expand, setExpand] = useState(expanded || false);
  const muiTheme = useTheme();
  const queryClient = useQueryClient();

  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const showMessageDialog = useMessageDialog((state) => state.showDialog);

  const { mutate: updateStageMutation, isPending } = useMutation({
    mutationFn: updateStage,
    onSuccess: () => {
      showSnackbar('Updated successfully!', 'success');
      queryClient.invalidateQueries({ queryKey: ['repair_tickets'] });
    },
  });

  const { mutate: sendAlert } = useMutation({
    mutationFn: sendMessage,
    onSuccess: () => showSnackbar('Alert sent successfully!', 'success'),
    onError: () => showSnackbar('Failed to send alert!', 'error'),
  });

  const status = ticket.status === "scheduled" ? ticket.status : ticket.task_status;
  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.scheduled;

  const [workTimer, setWorkTimer] = useState(
    calculateTotal(status, ticket?.active_start_time, ticket?.total_work_seconds) || 0
  );

  const curStep = ticket.status === "scheduled"
    ? 0
    : status !== "completed"
      ? ticket.current_repair_order - 1
      : ticket.current_repair_order;

  const totalSteps = ticket?.number_of_repairs || 1;
  const progressPct = Math.min(100, Math.max(0, (curStep / totalSteps) * 100));

  let buttonIcon, buttonText, step;
  if (status === "scheduled" || ticket.task_status === "accepted") {
    buttonIcon = <ArrowRightAltRoundedIcon />;
    buttonText = "Start";
    step = "start";
  } else if (ticket.task_status === "completed") {
    buttonIcon = <ArrowRightAltRoundedIcon />;
    buttonText = ticket.current_repair_order < ticket.number_of_repairs ? "Proceed" : "Complete";
    step = "proceed";
  } else {
    buttonIcon = <StopCircleRoundedIcon />;
    buttonText = "Complete";
    step = "complete";
  }

  const showTimer = ["completed", "in_progress", "paused"].includes(status) && !vendor;

  const handleShowDialog = () => {
    showMessageDialog({
      customer_id: ticket.customer_id,
      customer_name: ticket.client_name,
      resource: "repair_tickets",
      resource_id: ticket.repair_ticket_id,
    });
  };

  const viewCard = () => {
    setTicket(ticket);
    setOpen(true);
  };

  /* Live timer */
  useEffect(() => {
    let interval = null;
    if (status === 'in_progress') {
      interval = setInterval(() => {
        setWorkTimer(calculateTotal(status, ticket?.active_start_time, ticket?.total_work_seconds));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [status, ticket?.active_start_time, ticket?.total_work_seconds]);

  useEffect(() => {
    setWorkTimer(calculateTotal(status, ticket?.active_start_time, ticket?.total_work_seconds));
  }, [ticket?.total_work_seconds]);

  return (
    <TicketCard elevation={0} $expanded={expanded}>

      {/* ── Header: ticket # + WIG CODE (prominent) + status + menu ── */}
      <Header>
        <HeaderLeft>
          <TicketNum>#{ticket.repair_ticket_num}</TicketNum>
          <WigCode>
            <SellRoundedIcon />
            <span>{ticket.wig_id}</span>
          </WigCode>
        </HeaderLeft>
        <HeaderRight>
          <StatusPill $dotColor={statusCfg.dotColor} $textColor={statusCfg.textColor}>
            <StatusDot $color={statusCfg.dotColor} />
            {statusCfg.label}
          </StatusPill>
          {!expanded && (
            <Tooltip title="Expand">
              <IconButton size="small" onClick={() => setExpand(ex => !ex)} sx={{ p: 0.5 }}>
                <MoreVertRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </HeaderRight>
      </Header>

      {/* ── Body ── */}
      <Body>
        {/* Client + specialist + rep collapsed into one block */}
        <ClientRow>
          <Avatar>{getInitials(ticket.client_name)}</Avatar>
          <ClientInfo>
            <ClientName>{ticket.client_name}</ClientName>
            <ClientMeta>
              {[ticket.vendor_name || ticket.specialist_name, ticket.stylist_rep && `Rep: ${ticket.stylist_rep}`]
                .filter(Boolean)
                .join(' · ')}
            </ClientMeta>
          </ClientInfo>
        </ClientRow>

        {/* Service + appointment */}
        <DetailsPanel>
          <DetailRow>
            <DetailLabel>Repair</DetailLabel>
            <DetailValue>{ticket.repair_name}</DetailValue>
          </DetailRow>
          {ticket.due_date && (
            <DetailRow>
              <DetailLabel>Due Date</DetailLabel>
              <DetailValue>{formatGenericDate(ticket.due_date)}</DetailValue>
            </DetailRow>
          )}
        </DetailsPanel>

        {/* PROGRESS — promoted */}
        <ProgressSection>
          <ProgressHeader>
            <ProgressLabel>Repair Tasks</ProgressLabel>
            <ProgressCount>{curStep}/{totalSteps}</ProgressCount>
          </ProgressHeader>
          <PBTrack>
            <PBFill $width={`${progressPct}%`} 
            // $color={statusCfg.barColor} 
            $color={muiTheme.palette.success.main}
            />
          </PBTrack>
          {showTimer && (
            <TimerRow>
              <PunchClockRoundedIcon fontSize="small" />
              {formatDurationFromSeconds(workTimer)}
            </TimerRow>
          )}
        </ProgressSection>

        {/* Expanded content */}
        {expand && ticket.details && (
          <div>
            <InputLabel>Repair Details</InputLabel>
            <Notes>{ticket.details}</Notes>
          </div>
        )}
        {expand && (
          <TicketTimeline>
            <TimelineWithComments
              title="Repair Ticket History"
              resource="repair_tickets"
              resource_id={ticket.repair_ticket_id}
            />
          </TicketTimeline>
        )}
      </Body>

      {/* ── Footer ── */}
      <Footer>
        <QuickActions>
          <Tooltip title="View details">
            <IconButton size="small" onClick={viewCard} sx={{ p: 0.75 }}>
              <ManageSearchRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Message customer">
            <IconButton size="small" onClick={handleShowDialog} color="info" sx={{ p: 0.75 }}>
              <MapsUgcRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {status === "completed" && (
            <Dialog
              title="Notify Customer"
              toggle={
                <Tooltip title="Alert customer to come in">
                  <IconButton size="small" color="error" sx={{ p: 0.75 }}>
                    <NotificationsActiveRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
              saveText="Notify Customer"
              saveFunc={() => {
                sendAlert({
                  text: `Hi ${getFirstName(ticket.client_name)}, the initial repairs for your wig have been completed! Please stop by at your earliest convenience so we can make sure everything looks perfect before we proceed. See you soon!`,
                  resource: "repair_tickets",
                  resource_id: ticket.repair_ticket_id,
                  customer_id: ticket.customer_id,
                  message_type: "notification",
                });
              }}
            >
              <AlertText>
                By clicking the button below, you will notify the customer via SMS to come in for a follow up.
              </AlertText>
            </Dialog>
          )}
        </QuickActions>

        {!receive ? (
          <PrimaryActions>
            {(["accepted", "paused"].includes(ticket.task_status) ||
              (ticket.task_status === "in_progress" && vendor)) && (
              <Button
                size="small"
                startIcon={<UndoRoundedIcon />}
                loading={isPending}
                sx={{ fontSize: "1.2rem", minWidth: 0 }}
                color="warning"
                onClick={() => updateStageMutation({ repair_ticket_id: ticket.repair_ticket_id, step: "undo" })}
              >
                Undo
              </Button>
            )}

            {ticket.task_status === "in_progress" && !vendor && (
              <Button
                size="small"
                endIcon={<PauseRoundedIcon />}
                variant={expanded ? "contained" : "text"}
                color="warning"
                sx={{ fontSize: "1.2rem" }}
                onClick={() => updateStageMutation({ repair_ticket_id: ticket.repair_ticket_id, step: "pause" })}
              >
                Pause
              </Button>
            )}

            {ticket.task_status === "paused" && (
              <Button
                size="small"
                endIcon={<PlayCircleFilledRoundedIcon />}
                variant={expanded ? "contained" : "text"}
                color="success"
                sx={{ fontSize: "1.2rem" }}
                onClick={() => updateStageMutation({ repair_ticket_id: ticket.repair_ticket_id, step: "resume" })}
              >
                Resume
              </Button>
            )}

            <Button
              size="small"
              variant="outlined"
              disableElevation
              endIcon={buttonIcon}
              sx={{ fontSize: "1.2rem"}}
              color="tertiary"
              onClick={() => updateStageMutation({ repair_ticket_id: ticket.repair_ticket_id, step: step })}
            >
              {buttonText}
            </Button>
          </PrimaryActions>
        ) : (
          <Button
            size="small"
            endIcon={<UnarchiveRoundedIcon />}
            variant={expanded ? "contained" : "text"}
            sx={{ fontSize: "1.2rem" }}
            onClick={(e) => onClick(e, ticket)}
          >
            Receive
          </Button>
        )}
      </Footer>
    </TicketCard>
  );
};

export default KanbanTicket;